/**
 * The small vocabulary of shared pieces.
 *
 * Kept in one file because they are each a few lines and are always used
 * together. The one that carries real weight is `StatusBadge`: it is the only
 * thing that turns a tone into a colour, which is what keeps "Bilinmiyor" from
 * ever being painted red by a screen in a hurry.
 */

import type { ReactNode } from "react";
import type { StatusTone } from "@/i18n/terms";
import "./ui.css";

// --- badge -----------------------------------------------------------------

export function StatusBadge({
  tone,
  children,
  title,
}: {
  tone: StatusTone;
  children: ReactNode;
  title?: string | undefined;
}) {
  return (
    <span className={`badge badge--${tone}`} title={title}>
      <span className="badge__dot" aria-hidden="true" />
      {children}
    </span>
  );
}

// --- button ----------------------------------------------------------------

type ButtonProps = {
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  variant?: "primary" | "secondary" | "ghost" | "danger";
  disabled?: boolean;
  busy?: boolean;
  /** Full sentence explaining a disabled state. Never leave one unexplained. */
  title?: string | undefined;
  /**
   * An accessible name that overrides the visible label.
   *
   * For repeated controls whose visible text is identical - a column of
   * "İndir" buttons in an artifact table - where "İndir" alone tells a screen
   * reader user nothing about *which* file. The visible text stays short; the
   * announced name names the row.
   */
  ariaLabel?: string | undefined;
  /**
   * The id of an element that qualifies this button.
   *
   * For a control whose label is not the whole story - "Çözünürlüğü Artır 2x"
   * beside a sentence saying the detail is generated rather than recovered. A
   * sighted reader takes both in at once; without this a screen reader
   * announces the button and not the caveat.
   */
  ariaDescribedBy?: string | undefined;
};

export function Button({
  children,
  onClick,
  type = "button",
  variant = "secondary",
  disabled = false,
  busy = false,
  title,
  ariaLabel,
  ariaDescribedBy,
}: ButtonProps) {
  return (
    <button
      type={type}
      className={`btn btn--${variant}`}
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      title={title}
    >
      {busy && <Spinner inline />}
      {children}
    </button>
  );
}

// --- spinner ---------------------------------------------------------------

export function Spinner({ inline = false, label }: { inline?: boolean; label?: string }) {
  return (
    <span className={inline ? "spinner spinner--inline" : "spinner"} role="status">
      <span className="visually-hidden">{label ?? "Yükleniyor"}</span>
    </span>
  );
}

// --- callout ---------------------------------------------------------------

/**
 * A boxed note. `tone="neutral"` is the default because most callouts in this
 * product state a limitation rather than a problem - "there is no live camera
 * yet" is information, not a warning.
 */
export function Callout({
  tone = "neutral",
  title,
  children,
}: {
  tone?: StatusTone;
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className={`callout callout--${tone}`}>
      {title && <strong className="callout__title">{title}</strong>}
      <div className="callout__body">{children}</div>
    </div>
  );
}

// --- empty state -----------------------------------------------------------

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <p className="empty__title">{title}</p>
      {description && <p className="muted">{description}</p>}
      {action && <div className="empty__action">{action}</div>}
    </div>
  );
}

// --- loading placeholder ---------------------------------------------------

export function Loading({ label = "Yükleniyor" }: { label?: string }) {
  return (
    <div className="loading">
      <Spinner label={label} />
      <span className="muted">{label}…</span>
    </div>
  );
}

// --- definition list -------------------------------------------------------

export function DataList({ items }: { items: Array<{ term: string; value: ReactNode; help?: string }> }) {
  return (
    <dl className="datalist">
      {items.map((item) => (
        <div className="datalist__row" key={item.term}>
          <dt title={item.help}>{item.term}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

// --- meter -----------------------------------------------------------------

/**
 * A 0-1 value drawn as a bar.
 *
 * `aria-valuetext` carries the Turkish label plus the formatted number, so a
 * screen reader hears "Kapsama: %72" rather than a bare "0.72" with no unit
 * and no indication of which of the two metrics it is.
 */
export function Meter({
  value,
  tone,
  label,
  formatted,
}: {
  value: number;
  tone: StatusTone;
  label: string;
  formatted: string;
}) {
  const clamped = Math.max(0, Math.min(1, value));
  return (
    <div
      className="meter"
      role="meter"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={1}
      aria-valuetext={`${label}: ${formatted}`}
    >
      <div className={`meter__fill meter__fill--${tone}`} style={{ width: `${clamped * 100}%` }} />
    </div>
  );
}
